import React, { useMemo, useState, VFC } from 'react';
import theme from '@cubeyou-team/hx-theme';

import {
  Anchor,
  Box,
  Button,
  Icon,
  InfoLabel,
  Text,
  Row,
  Col,
  ButtonIcon,
  ItemPic,
  FollowersBadge,
  ItemTaxonomy,
} from '@cubeyou-team/hx-react-components';

import {
  StyledItemInfoBox,
  StyledCloseButtonContainer,
  ShowLessOrMoreButton,
} from './components';
import { ItemInfoBoxProps } from './types';
import { getItemStreams } from './utils';

export const ItemInfoBox: VFC<ItemInfoBoxProps> = ({
  item,
  descriptionFallbackText = '',
  errorReportButtonText = 'Report An Error',
  onClose,
  onErrorReportRequest,
}) => {
  const [showMore, setShowMore] = useState(false);
  const {
    label,
    description,
    taxonomy,
    type,
    pic,
    twitter_url,
    streams,
    feeds,
    followers_count,
    item_metadata,
  } = item;
  const itemStreams = (streams && getItemStreams(streams)) || [];
  const showDataSources =
    (itemStreams && itemStreams.length > 0) || twitter_url || (item_metadata && item_metadata.length > 0);

  const itemType = useMemo(() => {
    if (type === 'membership') {
      return 'conversation';
    }
    if (!type.includes('demographic')) {
      return type;
    }

    return 'demographic';
  }, [type]);

  const itemPic = useMemo(() => {
    let picture = null;
    if (feeds && feeds.length > 0) {
      const iosStore = feeds.find((f) => f.feed === 'ios');
      const androidStore = feeds.find((f) => f.feed === 'android');

      picture =
        (iosStore && iosStore.image_url) ||
        (androidStore && androidStore.image_url);
    }
    return picture || pic;
  }, [feeds, pic]);

  const itemDescription = useMemo(() => {
    let descr = null;
    if (feeds && feeds.length > 0) {
      const iosStore = feeds.find((f) => f.feed === 'ios');
      const androidStore = feeds.find((f) => f.feed === 'android');

      descr =
        (iosStore && iosStore.app_store_description) ||
        (androidStore && androidStore.app_store_description);
    }
    return descr || description || descriptionFallbackText;
  }, [feeds, description]);

  const items = ['ios', 'android'];
  const sortedFeeds =
    feeds &&
    feeds.sort((a, b) => items.indexOf(a.feed) - items.indexOf(b.feed));
  const showLessOrMoreButton = itemDescription && itemDescription.length > 500;

const PlatformIcon = {
  'instagram': 'instagram',
  'twitter': 'twitter_x',
}

  return (
    <StyledItemInfoBox>
      <StyledCloseButtonContainer>
        <ButtonIcon
          icon="remove"
          size="small"
          type="button"
          variant="dark_lighter"
          onClick={onClose}
        />
      </StyledCloseButtonContainer>
      <Row valign="middle">
        <Col span="auto">
          <ItemPic pic={itemPic} label={label} size="large" />
        </Col>
        <Col span="auto">
          <Text size="xbody" weight="bold">
            {label}
          </Text>
          <Row>
            <Col span="auto" style={{ padding: theme.spacing.xxsmall }}>
              <InfoLabel label={itemType} />
            </Col>
          </Row>
        </Col>
      </Row>
      <Row>
        <Col>
          <Box mT="medium">
            <ItemTaxonomy taxonomy={taxonomy} stacked truncated={false} />
          </Box>
        </Col>
      </Row>
      <Row>
        <Col>
          <Box pY="medium">
            <Text size="small">
              {showMore ? itemDescription : itemDescription.slice(0, 500)}
              {showLessOrMoreButton && (
                <ShowLessOrMoreButton
                  more={!showMore}
                  onClick={() => setShowMore(!showMore)}
                >
                  {showMore ? 'Show less' : '...Show more'}
                </ShowLessOrMoreButton>
              )}
            </Text>
          </Box>
        </Col>
      </Row>
      {followers_count && (
        <Box pY="medium">
          <FollowersBadge
            followers={followers_count}
            icon="star_outline"
            size="xsmall"
            radius="large"
            showTooltip={false}
            full={true}
          />
        </Box>
      )}
      {showDataSources && (
        <Box pB="medium">
          <Box pY="small">
            <Row valign="middle" gutter="xsmall">
              {itemStreams.map((stream) => {
                return (
                  <Col
                    key={`data-sources-${stream.id}`}
                    span="auto"
                    style={{ padding: theme.spacing.xxsmall }}
                  >
                    <Box pL="xsmall" pR="medium">
                      <Row valign="middle" gutter="xsmall">
                        <Col span="auto">
                          <Icon size="small" icon={stream.icon} />
                        </Col>
                        <Col span="auto">
                          <Text size="small">{stream.description}</Text>
                        </Col>
                      </Row>
                    </Box>
                  </Col>
                );
              })}
              {item_metadata?.map((item, index) => (
                <Col
                  key={`item-metadata-${item.sm_item_id || item.variable_id || index}`}
                  span="auto"
                  style={{ padding: theme.spacing.xxsmall }}
                >
                  <Box pL="xsmall" pR="medium">
                    <Row valign="middle" gutter="xsmall">
                      <Col span="auto">
                        <Icon size="small" icon={PlatformIcon[item.platform]} />
                      </Col>
                      <Col span="auto">
                        <Anchor target="_blank" href={item.source_url}>
                          {label}
                        </Anchor>
                      </Col>
                    </Row>
                  </Box>
                </Col>
              ))}
            </Row>
          </Box>
        </Box>
      )}
      {feeds && (
        <Box pB="medium">
          <Text size="small" weight="bold">
            Store
          </Text>
          <Box pY="small">
            <Row valign="middle" gutter="xsmall">
              {sortedFeeds.map((f) => (
                <Col key={`store-${f.feed}`} span="auto">
                  <Row valign="middle" gutter="xsmall">
                    <Col span="auto">
                      <Icon size="medium" icon={`${f.feed}_store`} />
                    </Col>
                    <Col span="auto">
                      {f.app_store_url ? (
                        <Anchor target="_blank" href={f.app_store_url}>
                          {f.feed === 'ios' ? 'iOS' : 'Android'}
                        </Anchor>
                      ) : (
                        <Text size="small">
                          {f.feed === 'ios' ? 'iOS' : 'Android'}
                        </Text>
                      )}
                    </Col>
                  </Row>
                </Col>
              ))}
            </Row>
          </Box>
        </Box>
      )}
      {onErrorReportRequest && (
        <Row>
          <Col>
            <Box pT="large">
              <Button
                size="small"
                type="button"
                variant="outlined"
                onClick={onErrorReportRequest}
              >
                {errorReportButtonText}
              </Button>
            </Box>
          </Col>
        </Row>
      )}
    </StyledItemInfoBox>
  );
};

ItemInfoBox.defaultProps = {
  descriptionFallbackText: '',
  errorReportButtonText: 'Report An Error',
};
